import { Building2, Ruler, ArrowRight, Play } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Two-card chooser shown when the user clicks a top-level "Try Demo" CTA on
 * the umbrella MyPlanCost page. Picking FlipCost vs SpaceCost hands the
 * caller back the chosen product so it can open the live `DemoDialog` with
 * the right seed, theme and tab set.
 *
 * Props:
 *  - open / onOpenChange — standard dialog controls.
 *  - onPick(product)     — called with `"flipcost"` or `"spacecost"`.
 */
export default function DemoChooserDialog({ open, onOpenChange, onPick }) {
  const choose = (product) => {
    onOpenChange(false);
    onPick?.(product);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="demo-chooser-dialog"
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px]"
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-2.5 py-0.5 w-fit">
            <Play className="w-3 h-3" /> Live demo · no signup
          </div>
          <DialogTitle className="fc-heading text-[#1C1C1C] text-xl mt-3">
            Which tool do you want to try?
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">
            SpaceCost is for single-room or single-space budgets. FlipCost is for whole-property renovations and flips.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <button
            type="button"
            data-testid="demo-chooser-spacecost"
            onClick={() => choose("spacecost")}
            data-theme="spacecost"
            className="text-left rounded-xl border border-black/[0.08] bg-white hover:border-sage-300 hover:bg-sage-50/40 p-5 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
              <Ruler className="w-4 h-4" />
            </div>
            <div className="fc-label text-sage-700 mt-3">SpaceCost</div>
            <div className="fc-heading text-[14px] text-[#1C1C1C] mt-1">Single-space demo</div>
            <p className="text-[12px] text-[#57534E] mt-2 leading-relaxed">
              Room-by-room budget for a kitchen, bathroom, garden or single extension.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              Try SpaceCost <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            type="button"
            data-testid="demo-chooser-flipcost"
            onClick={() => choose("flipcost")}
            className="text-left rounded-xl border border-black/[0.08] bg-white hover:border-sage-300 hover:bg-sage-50/40 p-5 transition-all"
          >
            <div className="w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
              <Building2 className="w-4 h-4" />
            </div>
            <div className="fc-label text-sage-700 mt-3">FlipCost</div>
            <div className="fc-heading text-[14px] text-[#1C1C1C] mt-1">Whole-property demo</div>
            <p className="text-[12px] text-[#57534E] mt-2 leading-relaxed">
              Profit, ROI, ARV. Rooms + extensions + exterior + systems + soft costs.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-[12px] text-[#1C1C1C]">
              Try FlipCost <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
